// The size, and so the rent, of a token account an action will create
// (G5 closure R52). An SPL Token account is always 165 bytes. A Token-2022
// account is 165 bytes, one account-type byte, then TLV extensions, and
// which extensions it must carry is decided by the mint (spl-token-2022's
// `ExtensionType::get_required_init_account_extensions`): a transfer-fee
// mint forces `TransferFeeAmount` on every account, a non-transferable mint
// forces `NonTransferableAccount`, a transfer-hook mint `TransferHookAccount`,
// a pausable mint `PausableAccount`. The associated-token-account program
// adds `ImmutableOwner` to every Token-2022 account it creates. A mint this
// table cannot price is not priced at all (R58): a mint extension it does
// not know, or a mint whose Token-2022 layout is malformed, throws
// `MintUnpriceable`, and the caller fails closed before anything is
// authorised. The table is the supported domain; nothing outside it is
// guessed at.

const TOKEN_2022_PROGRAM_B58 = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';

/** An SPL Token / Token-2022 token account's base layout. */
export const TOKEN_ACCOUNT_BASE_LEN = 165;
/** A mint's base layout: the whole of an SPL Token mint, or of a Token-2022 mint without extensions. */
export const MINT_BASE_LEN = 82;
/** Token-2022: the account-type byte after the base layout (1 = Mint, 2 = Account). */
const ACCOUNT_TYPE_OFFSET = 165;
const ACCOUNT_TYPE_MINT = 1;
/** Each TLV extension: type u16 le, length u16 le, data. */
const TLV_HEADER_LEN = 4;

/** Token-2022 `ExtensionType` numbers this module needs by name. */
export const EXTENSION = {
  TransferFeeConfig: 1,
  TransferFeeAmount: 2,
  ImmutableOwner: 7,
  NonTransferable: 9,
  NonTransferableAccount: 13,
  TransferHook: 14,
  TransferHookAccount: 15,
  Pausable: 26,
  PausableAccount: 27,
} as const;

/** Mint extensions that force an account extension on every new account, with that account extension's data length. */
const FORCED_ACCOUNT_EXTENSION_LEN: Readonly<Record<number, number>> = {
  [EXTENSION.TransferFeeConfig]: 8, // TransferFeeAmount: withheld_amount u64
  [EXTENSION.NonTransferable]: 0, // NonTransferableAccount (the ATA program's ImmutableOwner is already counted)
  [EXTENSION.TransferHook]: 1, // TransferHookAccount: transferring bool
  [EXTENSION.Pausable]: 0, // PausableAccount
};

/** Mint extensions known to force nothing on accounts: MintCloseAuthority, ConfidentialTransferMint, DefaultAccountState, InterestBearingConfig, PermanentDelegate, ConfidentialTransferFeeConfig, MetadataPointer, TokenMetadata, GroupPointer, TokenGroup, GroupMemberPointer, TokenGroupMember, ConfidentialMintBurn, ScaledUiAmount. */
const MINT_ONLY_EXTENSIONS: ReadonlySet<number> = new Set([3, 4, 6, 10, 12, 16, 18, 19, 20, 21, 22, 23, 24, 25]);

/** Why a token account's size, and so its rent, could not be determined from its mint (R58). The action is refused before authorization rather than priced by a guess. */
export class MintUnpriceable extends Error {
  constructor(
    readonly reason: 'missing' | 'malformed' | 'unknown-extension',
    readonly extensionType?: number,
  ) {
    super(
      reason === 'missing'
        ? 'The mint account could not be read, so the size of the token account this action would create is unknown. Nothing was signed; try again once the RPC endpoint answers.'
        : reason === 'malformed'
          ? 'The mint account does not have a valid Token-2022 layout, so the size of the token account this action would create is unknown. Nothing was signed.'
          : `The mint uses a Token-2022 extension this app does not know (type ${extensionType}), so the size of the token account this action would create is unknown. Nothing was signed.`,
    );
    this.name = 'MintUnpriceable';
  }
}

/**
 * The extension types a Token-2022 mint carries, in order. Exactly two
 * layouts are valid: the 82-byte base mint (an SPL Token mint, or a
 * Token-2022 mint without extensions), and the extended mint, the base
 * zero-padded to 165 bytes, the account-type byte Mint, then TLV entries.
 * Anything else is malformed and throws `MintUnpriceable` rather than
 * reading as extension-free (R58, R61): any other length up to 165, an
 * account-type byte that is not Mint, an entry that overruns the data, or
 * trailing bytes too short for a header that are not all zero. A zero
 * type ends the entries; short all-zero trailing bytes are padding.
 */
export function mintExtensionTypes(mintData: Uint8Array): number[] {
  if (mintData.length === MINT_BASE_LEN) return [];
  if (mintData.length <= ACCOUNT_TYPE_OFFSET || mintData[ACCOUNT_TYPE_OFFSET] !== ACCOUNT_TYPE_MINT) throw new MintUnpriceable('malformed');
  const view = new DataView(mintData.buffer, mintData.byteOffset, mintData.byteLength);
  const types: number[] = [];
  let off = ACCOUNT_TYPE_OFFSET + 1;
  while (off < mintData.length) {
    if (off + TLV_HEADER_LEN > mintData.length) {
      // Too short for another header: padding only if every remaining byte is zero.
      if (mintData.subarray(off).some((b) => b !== 0)) throw new MintUnpriceable('malformed');
      break;
    }
    const type = view.getUint16(off, true);
    const len = view.getUint16(off + 2, true);
    if (type === 0) break; // Uninitialized: the end of the entries
    if (off + TLV_HEADER_LEN + len > mintData.length) throw new MintUnpriceable('malformed');
    types.push(type);
    off += TLV_HEADER_LEN + len;
  }
  return types;
}

/**
 * The length of the associated token account `tokenProgram` will create for
 * `mintData`'s mint: 165 for SPL Token; for Token-2022, the base layout,
 * the account-type byte, `ImmutableOwner`, and every account extension the
 * mint forces. Rent follows from this length. Throws `MintUnpriceable`
 * for a malformed mint or one carrying an extension outside this table.
 */
export function associatedTokenAccountLen(tokenProgramB58: string, mintData: Uint8Array): number {
  if (tokenProgramB58 !== TOKEN_2022_PROGRAM_B58) return TOKEN_ACCOUNT_BASE_LEN;
  let len = TOKEN_ACCOUNT_BASE_LEN + 1 + TLV_HEADER_LEN; // account type, then ImmutableOwner (no data)
  for (const type of mintExtensionTypes(mintData)) {
    const forced = FORCED_ACCOUNT_EXTENSION_LEN[type];
    if (forced !== undefined) len += TLV_HEADER_LEN + forced;
    else if (!MINT_ONLY_EXTENSIONS.has(type)) throw new MintUnpriceable('unknown-extension', type);
  }
  return len;
}
